import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, PlusIcon, EditIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';

interface Profession {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  show_plans_tab: boolean;
  show_measurements_tab: boolean;
  show_custom_intake_tab: boolean;
  label_clients: string;
  label_plans: string;
  label_measurements: string;
  is_system_template: boolean;
}

export function ProfessionManagement() {
  const navigate = useNavigate();
  const { session, profile } = useAuth();

  // Fetch professions
  const { data: professionsData, isLoading } = useQuery({
    queryKey: ['admin', 'professions'],
    queryFn: async () => {
      const response = await apiRequest('/api/admin/professions', {}, session?.access_token);
      return response.professions as Profession[];
    },
    enabled: !!session?.access_token && profile?.role === 'admin',
  });

  const professions = professionsData || [];

  if (!profile || profile.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md">
          <p className="text-lg font-semibold text-gray-800 mb-2">Access Denied</p>
          <p className="text-gray-600 mb-6">You need admin privileges to access this page.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/admin')} className="text-gray-600 hover:text-gray-800">
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-semibold text-[#212121]">Profession Management</h1>
            </div>
            <button
              onClick={() => navigate('/admin/professions/create')}
              className="bg-[#5B8E87] text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-[#4F7A74] transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Custom Profession
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-6xl mx-auto">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading professions...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {professions.map((profession) => (
              <div
                key={profession.id}
                className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow"
                style={{ borderTop: `4px solid ${profession.color}` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {profession.icon && <span className="text-2xl">{profession.icon}</span>}
                    <div>
                      <h3 className="text-lg font-semibold text-[#212121]">{profession.name}</h3>
                      {profession.is_system_template && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          System Template
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/professions/${profession.id}/edit`)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Edit profession"
                  >
                    <EditIcon className="w-4 h-4" />
                  </button>
                </div>

                {profession.description && (
                  <p className="text-sm text-gray-600 mb-4">{profession.description}</p>
                )}

                {/* Tab Configuration */}
                <div className="space-y-2 mb-4">
                  <div className="text-xs font-medium text-gray-500 uppercase">Enabled Tabs</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md">
                      <CheckCircleIcon className="w-3 h-3" />
                      Messages
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md">
                      <CheckCircleIcon className="w-3 h-3" />
                      Appointments
                    </span>
                    {profession.show_plans_tab ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md">
                        <CheckCircleIcon className="w-3 h-3" />
                        {profession.label_plans}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
                        <XCircleIcon className="w-3 h-3" />
                        Plans
                      </span>
                    )}
                    {profession.show_measurements_tab ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md">
                        <CheckCircleIcon className="w-3 h-3" />
                        {profession.label_measurements}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
                        <XCircleIcon className="w-3 h-3" />
                        Measurements
                      </span>
                    )}
                  </div>
                </div>

                {/* Custom Labels */}
                <div className="space-y-1 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Clients:</span> {profession.label_clients}
                  </div>
                  <div>
                    <span className="font-medium">Plans:</span> {profession.label_plans}
                  </div>
                  <div>
                    <span className="font-medium">Measurements:</span> {profession.label_measurements}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
